import { addDays, differenceInCalendarDays, format, parseISO, startOfMonth, startOfWeek } from 'date-fns'
import { nowSP } from './spTime'

export type Periodo = 'hoje' | 'semana' | '7d' | 'mes' | '30d'

export const PERIODO_OPTIONS: Array<{ id: Periodo; label: string }> = [
  { id: 'hoje', label: 'Hoje' },
  { id: 'semana', label: 'Esta semana' },
  { id: '7d', label: 'Últimos 7 dias' },
  { id: 'mes', label: 'Este mês' },
  { id: '30d', label: 'Últimos 30 dias' },
]

/** Datas `yyyy-MM-dd` no calendário de São Paulo, com o fim incluído. */
export type Intervalo = { inicio: string; fim: string }

const iso = (d: Date) => format(d, 'yyyy-MM-dd')

/**
 * Mesma regra do Fiesta BI (`_normalizar_datas_periodo_pedidos` no fiesta-api):
 * o dia de hoje entra no intervalo e a semana começa na segunda. Assim os dois
 * sistemas mostram o mesmo recorte para o mesmo rótulo.
 */
export function intervaloDoPeriodo(periodo: Periodo, agora: Date = nowSP()): Intervalo {
  const fim = iso(agora)
  switch (periodo) {
    case 'hoje':
      return { inicio: fim, fim }
    case 'semana':
      return { inicio: iso(startOfWeek(agora, { weekStartsOn: 1 })), fim }
    case '7d':
      return { inicio: iso(addDays(agora, -6)), fim }
    case 'mes':
      return { inicio: iso(startOfMonth(agora)), fim }
    case '30d':
      return { inicio: iso(addDays(agora, -29)), fim }
  }
}

/** Quantos dias o intervalo cobre, contando início e fim. */
export function diasNoIntervalo({ inicio, fim }: Intervalo): number {
  return differenceInCalendarDays(parseISO(fim), parseISO(inicio)) + 1
}

/** O período de mesmo tamanho logo antes — base da comparação "vs período anterior". */
export function periodoAnterior(intervalo: Intervalo): Intervalo {
  const dias = diasNoIntervalo(intervalo)
  const inicio = parseISO(intervalo.inicio)
  return { inicio: iso(addDays(inicio, -dias)), fim: iso(addDays(inicio, -1)) }
}

/** Cada dia do intervalo, em ordem — para o eixo do gráfico não pular dia sem dado. */
export function diasDoIntervalo(intervalo: Intervalo): string[] {
  const inicio = parseISO(intervalo.inicio)
  return Array.from({ length: diasNoIntervalo(intervalo) }, (_, i) => iso(addDays(inicio, i)))
}

/** O mês civil anterior inteiro (para comparar com "Este mês"). */
export function intervaloMesPassado(agora: Date = nowSP()): Intervalo {
  const primeiro = startOfMonth(agora)
  return { inicio: iso(startOfMonth(addDays(primeiro, -1))), fim: iso(addDays(primeiro, -1)) }
}
